"use client";

import { useCallback, useEffect, useState } from "react";
import { useLocale, useTranslations } from "next-intl";

import { Button } from "@/components/ui/button";
import { ArrowRightIcon } from "@/components/ui/arrow-right"
import { formatAmount } from "@lib/util/money";
import { useTrialClaim } from "@modules/saas/trial-claim/context";
import { useConsent } from "@modules/common/components/consent/context";
import { useStartTrialBinding } from "@modules/saas/trial-claim/use-start-binding";
import { floatingBannerVisible } from "@lib/util/trial-phase";

/**
 * 4.7 悬浮试用通知(计划:announcement-banners-floating 区块的站点化包装)。
 *
 * - 状态逻辑抽成 `useTrialBanner`,供布局壳(MainShell)与横幅共用同一份
 *   dismiss 状态——布局据此决定是否给页脚留出底部 padding。
 * - SSR / 首帧渲染 null(mounted 门控),localStorage 只在客户端 effect 里
 *   读写并 try/catch 兜底(隐私模式等 storage 不可用场景静默降级),
 *   避免水合不匹配与读写抛错。
 */
export const TRIAL_BANNER_STORAGE_KEY = "trial-banner-dismissed";

export function useTrialBanner() {
  const [mounted, setMounted] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setMounted(true);
    try {
      setDismissed(
        window.localStorage.getItem(TRIAL_BANNER_STORAGE_KEY) === "true"
      );
    } catch {
      // storage 不可用(隐私模式/被禁用):视为未关闭,横幅照常显示。
    }
  }, []);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      window.localStorage.setItem(TRIAL_BANNER_STORAGE_KEY, "true");
    } catch {
      // 写入失败也不阻断 UI,本次会话内仍然消失。
    }
  }, []);

  return { mounted, dismissed, dismiss };
}

/**
 * 横幅本体。纯展示组件:onDismiss 由持有状态的布局壳传入,
 * 保证「点击不再显示」时横幅与页脚 padding 同帧消失。
 */
export default function FloatingTrialBanner({
  onDismiss,
}: {
  onDismiss: () => void;
}) {
  const t = useTranslations("TrialBanner");
  const locale = useLocale();
  const { open: openTrial, state } = useTrialClaim();
  const { busy } = useStartTrialBinding();

  // 2026-10-02 walkthrough (T06): once the trial is claimed but unbound, the
  // bar switches to the "bind a payment method" copy; the claim entry
  // (eligible, nothing claimed yet) keeps the acquisition copy.
  const sub = state?.subscription;
  const trialNotEnded =
    !sub?.trialEndsAt || new Date(sub.trialEndsAt).getTime() > Date.now();
  const bindPhase = Boolean(sub && !sub.hasPaymentMethod && trialNotEnded);

  // The claim phase has no payment method, so it must not promise a charge.
  // The bind phase has none either until the customer acts, so its notice is
  // conditional ("binding a payment method starts a subscription …"); a payload
  // without a price renders nothing rather than a half sentence.
  const claimPhase = Boolean(state?.authenticated && state.eligible);
  const disclosure = bindPhase
    ? state?.monthlyPrice
      ? t("autoRenewDisclosure", {
          price: formatAmount({
            amount: state.monthlyPrice.amount,
            currency_code: state.monthlyPrice.currencyCode,
            locale,
          }),
        })
      : null
    : claimPhase
      ? t("noChargeDisclosure")
      : null;

  // T05 (2026-10-02 walkthrough): a bound / paid / ended account stops seeing
  // the bar; anonymous visitors and the two offerable states keep it.
  if (!floatingBannerVisible(state)) return null;

  /**
   * The CTA carries two meanings (ISSUE.md A5), but both end in the same place:
   * the shared trial modal. In the bind phase it opens on `trial-bind-button`
   * (the +7-day flow), otherwise on the claim entry. It used to navigate to a
   * profile payment-methods section that no longer exists — a dead click.
   */
  const onCta = () => openTrial();

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 sm:flex sm:justify-center sm:px-6 sm:pb-5 lg:px-8"
      data-testid="trial-banner"
    >
      <div
        className="pointer-events-auto flex items-center justify-between gap-x-6 px-6 py-2.5 sm:rounded-xl sm:py-3 sm:pl-4 sm:pr-3.5"
        // Brand gradient (feedback 2026-09-28) — the white plate was wrong on
        // the black theme; this deep-blue sweep is the site's own palette.
        style={{
          background:
            "linear-gradient(to left, #002E5D, #002E5D, #2774ae)",
        }}
      >
        <p className="text-sm leading-6 text-white">
          <strong className="font-semibold">
            {bindPhase ? t("bindMessage") : t("message")}
          </strong>
          {disclosure ? (
            <span
              className="block text-xs leading-5 text-white/75"
              data-testid="trial-auto-renew-disclosure"
            >
              {disclosure}
            </span>
          ) : null}
        </p>
        <div className="flex items-center gap-x-2">
          <Button
            size="sm"
            className="flex items-center gap-x-1"
            onClick={onCta}
            disabled={busy}
            // ISSUE.md A5: the two phases mean different things — keep the
            // testids apart so automation never trips the bind entry by
            // accident (see e2e/helpers/testids.ts).
            data-testid={
              bindPhase ? "trial-banner-cta-bind" : "trial-banner-cta"
            }
          >
            {t("cta")}
            <ArrowRightIcon size={16} />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-white/70 hover:bg-white/10 hover:text-white"
            onClick={onDismiss}
            data-testid="trial-banner-dismiss"
          >
            {t("dismiss")}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * T05 (2026-10-02 walkthrough): the reserved strip and the bar must share ONE
 * visibility decision, and that decision needs the trial state - which only
 * exists INSIDE TrialClaimProvider. This slot is rendered inside the provider
 * by MainShell, so it owns both the footer-colored strip and the bar; an
 * account that is bound / paid / ended now loses the two together instead of
 * leaving an empty 56-80px strip above the footer.
 */
export function FloatingTrialBannerSlot() {
  const { mounted, dismissed, dismiss } = useTrialBanner();
  const { ready: consentReady, choice: consentChoice } = useConsent();
  const { state, loading } = useTrialClaim();

  // The consent banner occupies the same bottom edge; while the choice is
  // pending the trial bar yields (it is a one-time decision).
  const consentPending = !consentReady || consentChoice === null;
  // 2026-10-03 walkthrough (item 12): the eligibility payload is client-fetched,
  // so gating only on `mounted` rendered the bar for every visitor until the
  // response arrived — a signed-in bound/paid account saw it flash for ~0.5s on
  // every refresh. Require the payload (loading done AND state present) before
  // the visibility decision; SSR/first paint still renders nothing.
  const visible =
    mounted &&
    !loading &&
    state !== null &&
    !dismissed &&
    !consentPending &&
    floatingBannerVisible(state);

  if (!visible) return null;

  return (
    <>
      {/* Reserve the bar's height as a footer-colored strip so the gap reads
          as part of the footer rather than stray page bottom. */}
      <div aria-hidden className="h-14 shrink-0 bg-ui-bg-subtle sm:h-20" />
      <FloatingTrialBanner onDismiss={dismiss} />
    </>
  );
}
